import React, { useEffect, useState } from "react";
import { Outlet, useLocation } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import Sidebar from "./Sidebar";
import TopBar from "./TopBar";
import MobileNav from "./MobileNav";
import CommandPalette from "./CommandPalette";
import NeuralLines from "./NeuralLines";

const TITLES = {
  "/dashboard": { title: "Today", subtitle: "Your living memory · Spanish" },
  "/dashboard/memory": { title: "Memory graph", subtitle: "Everything AURA remembers" },
  "/dashboard/practice": { title: "Practice", subtitle: "Designed around your weak spots" },
  "/dashboard/progress": { title: "Progress", subtitle: "CEFR · signals · breakthroughs" },
  "/dashboard/goals": { title: "Goals", subtitle: "Where you're going" },
  "/dashboard/settings": { title: "Settings", subtitle: "Profile · voice print · privacy" },
};

const DashboardLayout = () => {
  const location = useLocation();
  const meta = TITLES[location.pathname] || { title: "AURA", subtitle: "" };

  const [collapsed, setCollapsed] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const [paletteOpen, setPaletteOpen] = useState(false);

  useEffect(() => {
    const onKey = (e) => {
      if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === "k") {
        e.preventDefault();
        setPaletteOpen((v) => !v);
      }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, []);

  return (
    <div className="relative min-h-screen bg-base text-ink" data-testid="dashboard-layout">
      <NeuralLines />
      <div aria-hidden className="pointer-events-none fixed inset-0 z-[1]" style={{ background: "linear-gradient(180deg, rgb(var(--bg-rgb) / 0.7) 0%, rgb(var(--bg-rgb) / 0.92) 100%)" }} />

      <div className="relative z-10 flex">
        <Sidebar
          collapsed={collapsed}
          onToggle={() => setCollapsed((v) => !v)}
          mobileOpen={mobileOpen}
          onMobileClose={() => setMobileOpen(false)}
        />

        <div className="flex-1 min-w-0">
          <TopBar
            title={meta.title}
            subtitle={meta.subtitle}
            onOpenSidebar={() => setMobileOpen(true)}
            onOpenPalette={() => setPaletteOpen(true)}
          />

          <AnimatePresence mode="wait">
            <motion.main
              key={location.pathname}
              initial={{ opacity: 0, y: 8 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -4 }}
              transition={{ duration: 0.28, ease: [0.2, 0.8, 0.2, 1] }}
              className="px-4 sm:px-6 lg:px-8 pt-6 pb-28 lg:pb-12"
              data-testid="dashboard-main"
            >
              <Outlet />
            </motion.main>
          </AnimatePresence>
        </div>
      </div>

      <MobileNav />
      <CommandPalette open={paletteOpen} onClose={() => setPaletteOpen(false)} />
    </div>
  );
};

export default DashboardLayout;
